import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import {
  ArrowBackRounded,
  ChevronRightRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Elements from '../../../Elements';
import Images from '../../../../Configs/images';
import ROUTES from '../../../../Configs/routes';
import { getUserData } from '../../../../Utils/storages';
import { getAddress } from '../../../../Redux/Models/address';
import { getCategories, getConditions } from '../../../../Redux/Models/master';
import { createRequestIn } from '../../../../Redux/Models/request';
import AlertContext from '../../../../Context/AlertContext';

export default function RequestStorage({
  classes, error, message, newRequest,
  isLoading,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { address, master } = useSelector((state) => state);
  const { openAlert } = useContext(AlertContext);

  const [selectAddress, setSelectAddress] = useState('');
  const [confirmation, setConfirmation] = useState(0);
  const [activeStep, setActiveStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [fields, setFields] = useState({
    owner: '',
    phone_number: '',
    owner_address: {},
    pickup_address: {},
    pickup_date: new Date(),
    pickup_notes: '',
    amount: 0,
    item_notes: '',
    category_id: '-',
    condition_id: '-',
  });

  const closeConfirmation = () => {
    setConfirmation(0);
  };

  const toggleSelectAddress = (newVal) => {
    setSelectAddress(newVal);
  };

  const selectPickupAddress = (adrs) => {
    if (selectAddress === 'pickup') {
      setFields({
        ...fields,
        pickup_address: adrs,
      });
    } else if (selectAddress === 'owner') {
      setFields({
        ...fields,
        owner_address: adrs,
      });
    }
    toggleSelectAddress('');
  };

  useEffect(() => {
    if (address.data.length) {
      const selectedAddress = address.data.filter((addr) => addr.is_default)[0];
      setFields({
        ...fields,
        owner_address: selectedAddress,
      });

      if (!fields.pickup_address.address) {
        setFields({
          ...fields,
          pickup_address: selectedAddress,
        });
      }
    }
  }, [address]);

  const handleChangeField = ({ target }) => {
    setFields({
      ...fields,
      [target.name]: target.value,
    });
  };

  const userData = getUserData();

  useEffect(() => {
    if (userData) {
      const owner = userData.back_name ? `${userData.front_name} ${userData.back_name}` : userData.front_name;
      setFields({
        ...fields,
        owner,
        phone_number: userData.phone_number,
      });
    }
  }, []);

  useEffect(() => {
    dispatch(getAddress(userData.id));
    dispatch(getCategories());
    dispatch(getConditions());
  }, []);

  const stepBack = () => {
    setActiveStep(activeStep - 1);
  };

  const wantToContinue = () => {
    let valid = false;
    switch (activeStep) {
      case 1: valid = fields.owner && fields.phone_number && fields.owner_address?.address;
        break;
      case 2: valid = fields.category_id !== '-' && fields.condition_id !== '-' && fields.item_notes && fields.amount;
        break;
      case 3: valid = fields.pickup_date && fields.pickup_notes;
        break;
      default: valid = false;
    }

    if (valid) {
      setActiveStep(activeStep + 1);
    } else {
      openAlert({
        message: 'Please complete the form!',
        severity: 'error',
      });
    }
  };

  const getImage = (name, step) => {
    if (step === activeStep) return Images[`${name}Active`];
    if (step > activeStep) return Images[name];
    return Images.Check;
  };

  const renderAddressList = () => {
    if (address.data.length) {
      return (
        <div className="mt-2">
          {address.data.map((adrs, index) => (
            <Box className="address-card" key={index} onClick={() => selectPickupAddress(adrs)}>
              <div className="flex space-between align-items-center">
                <div className="s-12 weight-700">
                  Address
                  {' '}
                  {index + 1}
                </div>
                <div hidden={!address.is_default} className="main-address s-14 weight-500 white">
                  Main address
                </div>
              </div>
              <div className="mt-3 s-16 weight-400">
                {adrs.address}
                ,
                {' '}
                {adrs.city}
                ,
                {' '}
                {adrs.province}
                ,
                {' '}
                {adrs.country}
                ,
                {' '}
                {adrs.postal_code}
              </div>
            </Box>
          ))}
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    if (error && submitted) {
      openAlert({
        open: true,
        severity: 'error',
        message: `Something went wrong, ${message}`,
      });
    }

    if (newRequest && submitted) {
      navigate(ROUTES.home(), 'replace');
    }
  }, [error, submitted, newRequest]);

  const submitRequest = () => {
    setSubmitted(true);
    const body = {
      ...fields,
      pickup_date: new Date(fields.pickup_date),
      owner_address: fields.owner_address.id,
      pickup_address: fields.pickup_address.id,
      amount: Number(fields.amount),
      type: 'Request In',
    };
    dispatch(createRequestIn(userData.id, body));
  };

  return (
    <Elements.PageBase noNav>
      <div className={classes.root}>
        <Container>
          <div className="flex align-items-center">
            <IconButton sx={{ p: 0 }} onClick={() => setConfirmation(2)}>
              <ArrowBackRounded className="black" />
            </IconButton>
            <h4>Request storage</h4>
          </div>
          <div className="mt-4">
            <div className="flex space-between align-items-center">
              <div className="center">
                <img src={getImage('Person', 1)} alt="Owner data" />
                <div className={`mt-1 s-12 ${activeStep === 1 && 'linear-red-text weight-700'}`}>Owner data</div>
              </div>
              <img src={Images.Divider} alt="Divider" />
              <div className="center">
                <img src={getImage('DataCheck', 2)} alt="Item data" />
                <div className={`mt-1 s-12 ${activeStep === 2 && 'linear-red-text weight-700'}`}>Item data</div>
              </div>
              <img src={Images.Divider} alt="Divider" />
              <div className="center">
                <img src={getImage('Calendar', 3)} alt="Pickup date" />
                <div className={`mt-1 s-12 ${activeStep === 3 && 'linear-red-text weight-700'}`}>Pickup date</div>
              </div>
              <img src={Images.Divider} alt="Divider" />
              <div className="center">
                <img src={getImage('AllCheck', 4)} alt="Confirmation" />
                <div className={`mt-1 s-12 ${activeStep === 4 && 'linear-red-text weight-700'}`}>Confirmation</div>
              </div>
            </div>
          </div>

          <div className="mt-4">

            <form>
              <div className="form-section" hidden={activeStep !== 1}>
                <div className="flex space-between">
                  <h4>Owners data</h4>
                  <IconButton sx={{ p: 0 }} onClick={() => navigate(`${ROUTES.profile()}/update`)}>
                    <img src={Images.Write} alt="write" />
                  </IconButton>
                </div>
                <label className="mt-3">Name</label>
                <TextField disabled value={fields.owner} placeholder="No name" fullWidth />
                <label className="mt-3">Phone number</label>
                <TextField disabled value={fields.phone_number} placeholder="No phone number" fullWidth />
                <label className="mt-3">Home address</label>
                <TextField
                  disabled
                  value={fields.owner_address.address}
                  onClick={() => toggleSelectAddress('owner')}
                  placeholder={address.isLoading ? 'Loading...' : 'No address'}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => toggleSelectAddress('owner')}>
                        <ChevronRightRounded />
                      </IconButton>
                    ),
                  }}
                />
                <div className="floating-action">
                  <Container>
                    <Button fullWidth size="large" variant="contained" className="btn-contained" onClick={wantToContinue}>
                      Next
                    </Button>
                  </Container>
                </div>
              </div>

              <div className="form-section" hidden={activeStep !== 2}>
                <div>
                  <h4>Item data</h4>
                </div>
                <label className="mt-3">Amount</label>
                <TextField placeholder="99" fullWidth value={fields.amount} name="amount" onChange={handleChangeField} InputLabelProps={{ inputMode: 'decimal' }} />
                <label className="mt-3">Notes</label>
                <TextField placeholder="My product is fragile" fullWidth value={fields.item_notes} name="item_notes" onChange={handleChangeField} />

                <div className="mt-5">
                  <h4>Item handling</h4>
                </div>
                <label className="mt-3">Item category</label>
                <TextField fullWidth select value={fields.category_id} name="category_id" onChange={handleChangeField}>
                  <MenuItem value="-" disabled>Choose category</MenuItem>
                  {master.categories.map((category) => (
                    <MenuItem value={category.id} key={category.id}>{category.name}</MenuItem>
                  ))}
                </TextField>
                <label className="mt-3">Item condition</label>
                <TextField fullWidth select value={fields.condition_id} name="condition_id" onChange={handleChangeField}>
                  <MenuItem value="-" disabled>Choose condition</MenuItem>
                  {master.conditions.map((condition) => (
                    <MenuItem value={condition.id} key={condition.id}>{condition.name}</MenuItem>
                  ))}
                </TextField>

                <div className="floating-action">
                  <Container>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Button fullWidth size="large" variant="outlined" className="btn-outlined" onClick={stepBack}>
                          Back
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button fullWidth size="large" variant="contained" className="btn-contained" onClick={wantToContinue}>
                          Next
                        </Button>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </div>

              <div className="form-section" hidden={activeStep !== 3}>
                <div>
                  <h4>Pickup date</h4>
                </div>
                <label className="mt-3">Date</label>
                <TextField type="datetime-local" fullWidth value={fields.pickup_date} name="pickup_date" onChange={handleChangeField} />
                <label className="mt-3">Notes</label>
                <TextField placeholder="Please on time. I wanna go outside" fullWidth value={fields.pickup_notes} name="pickup_notes" onChange={handleChangeField} />
                <label className="mt-3">Pickup address</label>
                <TextField
                  fullWidth
                  disabled
                  onClick={() => toggleSelectAddress('pickup')}
                  onChange={handleChangeField}
                  value={fields.pickup_address.address}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => toggleSelectAddress('pickup')}>
                        <ChevronRightRounded />
                      </IconButton>
                    ),
                  }}
                />
                <div className="floating-action">
                  <Container>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Button fullWidth size="large" variant="outlined" className="btn-outlined" onClick={stepBack}>
                          Back
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button fullWidth size="large" variant="contained" className="btn-contained" onClick={wantToContinue}>
                          Next
                        </Button>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </div>

              <div className="form-section" hidden={activeStep !== 4}>
                <div>
                  <h4>Confirmation</h4>
                </div>

                <div className="mt-4">
                  <div className="s-16 weight-600">Owner data</div>
                  <div className="mt-3">
                    <div className="s-12">Name</div>
                    <div className="height-14 weight-500">{fields.owner}</div>
                  </div>
                  <div className="mt-3">
                    <div className="s-12">Phone number</div>
                    <div className="height-14 weight-500">{fields.phone_number}</div>
                  </div>
                  <div className="mt-3">
                    <div className="s-12">Address</div>
                    <div className="height-14 weight-500">{fields.owner_address?.address}</div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="s-16 weight-600">Items info</div>
                  <div className="mt-3">
                    <div className="s-12">Item amounts</div>
                    <div className="height-14 weight-500">
                      {fields.amount}
                      {' '}
                      Items
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="s-12">Notes</div>
                    <div className="height-14 weight-500">{fields.item_notes}</div>
                  </div>
                  <div className="mt-3">
                    <div className="s-12">Category</div>
                    <div className="height-14 weight-500">{master.categories.filter((cat) => cat.id === fields.category_id)[0]?.name}</div>
                  </div>
                  <div className="mt-3">
                    <div className="s-12">Condition</div>
                    <div className="height-14 weight-500">{master.conditions.filter((con) => con.id === fields.condition_id)[0]?.name}</div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="s-16 weight-600">Pickup info</div>
                  <div className="mt-3">
                    <div className="s-12">Date</div>
                    <div className="height-14 weight-500">{moment(fields.pickup_date).format('HH.mm - DD-MM-YYYY')}</div>
                  </div>
                  <div className="mt-3">
                    <div className="s-12">Notes</div>
                    <div className="height-14 weight-500">{fields.pickup_notes}</div>
                  </div>
                  <div className="mt-3">
                    <div className="s-12">Address</div>
                    <div className="height-14 weight-500">{fields.pickup_address?.address}</div>
                  </div>
                </div>

                <div className="floating-action">
                  <Container>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Button fullWidth size="large" variant="outlined" className="btn-outlined" onClick={stepBack}>
                          Back
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button fullWidth size="large" variant="contained" className="btn-contained" onClick={() => setConfirmation(1)}>
                          Confirm
                        </Button>
                      </Grid>
                    </Grid>
                  </Container>
                </div>
              </div>
            </form>
          </div>
        </Container>

      </div>

      <Elements.ConfirmationModal
        open={confirmation === 1}
        desc={(
          <div>
            Make sure all of the Request In
            <br />
            items data are right and filled?
          </div>
        )}
        handleClose={closeConfirmation}
        handleAgree={submitRequest}
        isLoading={isLoading}
      />

      <Elements.ConfirmationModal
        open={confirmation === 2}
        desc={(
          <div>
            Are you sure want to cancel?
            <br />
            your progress will not saved
          </div>
        )}
        handleClose={closeConfirmation}
        handleAgree={() => navigate(-1)}
      />

      <Elements.ModalBase open={!!selectAddress} handleClose={() => toggleSelectAddress('')} noAgree maxWidth="xs">
        <div className={classes.address}>
          {renderAddressList()}
        </div>
      </Elements.ModalBase>
    </Elements.PageBase>
  );
}
